/**
 * Image URL Utilities
 * ===================
 * Helper functions for generating image URLs.
 */

import type { Product, ProductImage } from '@/types'

/**
 * Get the display URL for a product image.
 *
 * Uses the ID-based endpoint for local uploads to avoid reverse proxy issues.
 * External URLs (http/https) are returned as-is.
 */
export function getImageDisplayUrl(
  image: ProductImage,
  productId: number | string
): string {
  // External URLs - use as-is
  if (
    image.image_url.startsWith('http://') ||
    image.image_url.startsWith('https://')
  ) {
    return image.image_url
  }
  // Local uploads - use ID-based endpoint
  return `/api/products/${productId}/image/${image.id}/file`
}

/**
 * Get the thumbnail URL for a product.
 *
 * Uses the primary image if available, otherwise falls back to the first image.
 * Returns null if no images are available.
 */
export function getProductThumbnailUrl(product: Product): string | null {
  if (!product.images || product.images.length === 0) {
    return null
  }

  // Find primary image or use first image
  const primaryImage =
    product.images.find((img) => img.is_primary) || product.images[0]

  return getImageDisplayUrl(primaryImage, product.id)
}
